(function () {
  'use strict';

  var configPromises = new Map();
  var legacyPdpPriceConfigs = new WeakMap();

  function numericId(value) {
    if (value == null) return '';
    var match = String(value).match(/(\d+)(?!.*\d)/);
    return match ? match[1] : '';
  }

  function clampPercent(value) {
    return Math.max(0, Math.min(100, Number(value || 0)));
  }

  function discountedLineTotal(unitPrice, quantity, percent) {
    var safeUnitPrice = Math.max(0, Math.round(Number(unitPrice || 0)));
    var safeQuantity = Math.max(0, Math.round(Number(quantity || 0)));
    var unitDiscount = Math.floor(safeUnitPrice * clampPercent(percent) / 100);
    return Math.max(0, (safeUnitPrice - unitDiscount) * safeQuantity);
  }

  function formatThemeMoney(cents, moneyFormat) {
    var amount = Math.max(0, Number(cents || 0)) / 100;
    var decimalAmount = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    var noDecimals = Math.round(amount).toLocaleString();
    var commaAmount = decimalAmount.replace(/,/g, ' ').replace('.', ',').replace(/ /g, '.');
    var format = String(moneyFormat || '$__amount__').replace(/<[^>]*>/g, '');
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commaAmount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/,/g, '.'))
      .replace(/\{\{\s*amount\s*\}\}/g, decimalAmount)
      .replace('__amount__', decimalAmount);
  }

  function purchaseOptionMatches(offer, requestedMode) {
    var offerMode = String((offer && offer.purchaseOptionMode) || 'both');
    var mode = requestedMode === 'subscription' || requestedMode === 'one_time' ? requestedMode : 'both';
    return offerMode === 'both' || mode === 'both' || offerMode === mode;
  }

  function variantCollectionIds(config, variantId) {
    var id = numericId(variantId);
    var map = config && config.variantCollectionIdsByVariantId;
    var values = id && map && (map[id] || map[String(variantId || '')]);
    return Array.isArray(values) ? values.map(numericId).filter(Boolean) : [];
  }

  function normalizedConditions(conditions) {
    if (!conditions) {
      return { mode: 'all', items: [], collectionItems: [], minTotalQuantity: 0, minSubtotalCents: 0 };
    }
    if (Array.isArray(conditions)) {
      return { mode: 'all', items: conditions, collectionItems: [], minTotalQuantity: 0, minSubtotalCents: 0 };
    }
    return {
      mode: conditions.mode || 'all',
      items: Array.isArray(conditions.items) ? conditions.items : [],
      collectionItems: Array.isArray(conditions.collectionItems) ? conditions.collectionItems : [],
      minTotalQuantity: Number(conditions.minTotalQuantity || 0),
      minSubtotalCents: Number(conditions.minSubtotalCents || 0)
    };
  }

  function cartFromLines(config, lines) {
    var grouped = new Map();
    (lines || []).forEach(function (line) {
      var id = numericId(line && (line.variant_id || line.id));
      if (!id) return;
      var existing = grouped.get(id) || { variant_id: id, quantity: 0, price: Number(line.price || 0) };
      existing.quantity += Math.max(1, Number(line.quantity || 1));
      if (!existing.price) existing.price = Number(line.price || 0);
      grouped.set(id, existing);
    });
    return {
      items: Array.from(grouped.values()).map(function (item) {
        item.collectionIds = variantCollectionIds(config, item.variant_id);
        item.line_price = item.price * item.quantity;
        item.final_line_price = item.line_price;
        return item;
      })
    };
  }

  function eligible(offer, cart, purchaseOptionMode) {
    if (!purchaseOptionMatches(offer, purchaseOptionMode)) return false;
    var conditions = normalizedConditions(offer && offer.conditions);
    var subtotal = (cart.items || []).reduce(function (total, item) {
      return total + Number(item.final_line_price || item.line_price || 0);
    }, 0);
    if (conditions.minSubtotalCents > 0 && subtotal < conditions.minSubtotalCents) return false;
    if (!conditions.items.length && !conditions.collectionItems.length) return true;

    var byVariant = {};
    var byCollection = {};
    (cart.items || []).forEach(function (item) {
      var variantId = numericId(item.variant_id);
      var quantity = Number(item.quantity || 0);
      if (!variantId) return;
      byVariant[variantId] = (byVariant[variantId] || 0) + quantity;
      (item.collectionIds || []).forEach(function (collectionId) {
        var id = numericId(collectionId);
        if (id) byCollection[id] = (byCollection[id] || 0) + quantity;
      });
    });

    if (conditions.mode === 'any_total') {
      var total = conditions.items.reduce(function (quantity, item) {
        return quantity + (byVariant[numericId(item && item.variantId)] || 0);
      }, 0);
      total += conditions.collectionItems.reduce(function (quantity, item) {
        return quantity + (byCollection[numericId(item && item.collectionId)] || 0);
      }, 0);
      return total >= (conditions.minTotalQuantity || 1);
    }

    var variantsMet = conditions.items.every(function (item) {
      var required = Number((item && (item.minQuantity || item.quantity)) || 1);
      return (byVariant[numericId(item && item.variantId)] || 0) >= required;
    });
    var collectionsMet = conditions.collectionItems.every(function (item) {
      var required = Number((item && (item.minQuantity || item.quantity)) || 1);
      return (byCollection[numericId(item && item.collectionId)] || 0) >= required;
    });
    return variantsMet && collectionsMet;
  }

  function tierPercent(tier) {
    return clampPercent(
      tier && (
        (tier.rewards && tier.rewards.discountPercent) ||
        tier.percentOff ||
        tier.discountPercent
      )
    );
  }

  function tierGiftQuantity(tier) {
    var gift = tier && tier.rewards && tier.rewards.freeGift;
    var choices = gift && Array.isArray(gift.choices) ? gift.choices
      : [gift && gift.giftVariantId || tier && tier.giftVariantId];
    var available = choices.some(function (choice) {
      return choice && choice.storefrontAvailable !== false && numericId(choice.variantId || choice.id || choice);
    });
    var quantity = Number(gift && gift.quantity || 1);
    return available && Number.isFinite(quantity) ? Math.max(1, quantity) : 0;
  }

  function bestTierPercent(offer, cart) {
    var quantity = Math.max(1, (cart.items || []).reduce(function (total, item) {
      return total + Number(item.quantity || 0);
    }, 0));
    return (Array.isArray(offer && offer.tiers) ? offer.tiers : []).reduce(function (best, tier) {
      var minimum = Number(tier && tier.minQuantity);
      if (!Number.isFinite(minimum) || minimum < 1) return best;
      return quantity >= minimum
        ? Math.max(best, tierPercent(tier))
        : best;
    }, 0);
  }

  function normalizePath(path) {
    var value = String(path || '/').split('?')[0].toLowerCase();
    if (!value.startsWith('/')) value = '/' + value;
    return value.length > 1 && value.endsWith('/') ? value.slice(0, -1) : value;
  }

  function pageRuleMatches(rule, locationLike) {
    if (!rule) return false;
    var raw = String(rule).trim().toLowerCase();
    var queryIndex = raw.indexOf('?');
    var rulePath = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
    var ruleQuery = queryIndex >= 0 ? raw.slice(queryIndex + 1) : '';
    var isPrefix = rulePath.endsWith('*');
    if (isPrefix) rulePath = rulePath.slice(0, -1);
    var currentPath = normalizePath(locationLike.pathname);
    var expectedPath = normalizePath(rulePath);
    var pathMatches = isPrefix
      ? currentPath === expectedPath || currentPath.startsWith(expectedPath + '/')
      : currentPath === expectedPath;
    if (!pathMatches) return false;

    var expected = new URLSearchParams(ruleQuery);
    var expectedView = String(expected.get('view') || '').trim().toLowerCase();
    if (!expectedView) return true;
    var current = new URLSearchParams(locationLike.search || '');
    return String(current.get('view') || '').trim().toLowerCase() === expectedView;
  }

  function activePageOfferIds(config, locationLike) {
    return new Set((Array.isArray(config && config.pageOffers) ? config.pageOffers : [])
      .filter(function (offer) {
        return offer && offer.id && Array.isArray(offer.pagePaths) && offer.pagePaths.some(function (rule) {
          return pageRuleMatches(rule, locationLike);
        });
      })
      .map(function (offer) { return String(offer.id); }));
  }

  function productOfferMatches(offer, variantId, purchaseOptionMode, config) {
    if (!offer || offer.recommendationOnly || !purchaseOptionMatches(offer, purchaseOptionMode)) return false;
    variantId = numericId(variantId);
    if (!variantId) return false;
    var targetMatched = (Array.isArray(offer.targets) ? offer.targets : []).some(function (target) {
      return numericId(target) === variantId;
    });
    var collectionTargets = Array.isArray(offer.collectionTargets)
      ? offer.collectionTargets
      : (Array.isArray(offer.targetCollectionIds) ? offer.targetCollectionIds : []);
    if (!targetMatched && collectionTargets.length) {
      var collections = variantCollectionIds(config, variantId);
      targetMatched = collectionTargets.some(function (target) {
        return collections.indexOf(numericId(target)) >= 0;
      });
    }
    return targetMatched;
  }

  function productOfferPercent(offer, variant, cart, purchaseOptionMode, config) {
    if (!productOfferMatches(offer, variant && variant.id, purchaseOptionMode, config) || !eligible(offer, cart, purchaseOptionMode)) return 0;
    var tier = bestTierPercent(offer, cart);
    if (tier > 0) return tier;
    if (String(offer.valueType || 'percentage') === 'fixed') {
      var price = Number(variant.price || 0);
      return price > 0 ? clampPercent((Number(offer.value || 0) * 100 / price) * 100) : 0;
    }
    return clampPercent(offer.value);
  }

  function pageOfferPercent(offer, variant, cart, pageOfferIds, purchaseOptionMode) {
    if (!offer || !pageOfferIds.has(String(offer.id))) return 0;
    if (!eligible(offer, cart, purchaseOptionMode)) return 0;
    return bestTierPercent(offer, cart);
  }

  function quote(config, lines, options) {
    var safeConfig = config || {};
    var purchaseOptionMode = (options && options.purchaseOptionMode) || 'one_time';
    var locationLike = (options && options.location) || window.location;
    var cart = cartFromLines(safeConfig, lines);
    var pageOfferIds = options && options.pageOfferId
      ? new Set([String(options.pageOfferId)])
      : activePageOfferIds(safeConfig, locationLike);
    var quotedLines = cart.items.map(function (item) {
      var variant = { id: item.variant_id, price: item.price };
      var percent = 0;
      (Array.isArray(safeConfig.productOffers) ? safeConfig.productOffers : []).forEach(function (offer) {
        percent = Math.max(percent, productOfferPercent(offer, variant, cart, purchaseOptionMode, safeConfig));
      });
      (Array.isArray(safeConfig.pageOffers) ? safeConfig.pageOffers : []).forEach(function (offer) {
        percent = Math.max(percent, pageOfferPercent(offer, variant, cart, pageOfferIds, purchaseOptionMode));
      });
      var regularTotal = Math.round(Number(item.price || 0) * Number(item.quantity || 0));
      return {
        id: item.variant_id,
        quantity: item.quantity,
        discountPercent: clampPercent(percent),
        regularTotal: regularTotal,
        discountedTotal: discountedLineTotal(item.price, item.quantity, percent)
      };
    });
    return {
      discountPercent: quotedLines.reduce(function (best, line) {
        return Math.max(best, line.discountPercent);
      }, 0),
      regularTotal: quotedLines.reduce(function (total, line) { return total + line.regularTotal; }, 0),
      discountedTotal: quotedLines.reduce(function (total, line) { return total + line.discountedTotal; }, 0),
      lines: quotedLines,
      activePageOfferIds: Array.from(pageOfferIds)
    };
  }

  function quantityTiers(config, variant, options) {
    var safeConfig = config || {};
    var variantId = numericId(variant && variant.id);
    if (!variantId) return [];
    var purchaseOptionMode = (options && options.purchaseOptionMode) || 'one_time';
    var pageOfferIds = options && options.pageOfferId
      ? new Set([String(options.pageOfferId)])
      : activePageOfferIds(safeConfig, (options && options.location) || window.location);
    var offers = (safeConfig.productOffers || []).filter(function (offer) {
      return productOfferMatches(offer, variantId, purchaseOptionMode, safeConfig);
    }).concat((safeConfig.pageOffers || []).filter(function (offer) {
      return offer && pageOfferIds.has(String(offer.id)) && purchaseOptionMatches(offer, purchaseOptionMode);
    }));
    var quantities = new Set();
    offers.forEach(function (offer) {
      (Array.isArray(offer.tiers) ? offer.tiers : []).forEach(function (tier) {
        var rawQuantity = Number(tier && tier.minQuantity);
        if (!Number.isFinite(rawQuantity) || rawQuantity < 1) return;
        if (tierPercent(tier) > 0 || tierGiftQuantity(tier) > 0) quantities.add(Math.round(rawQuantity));
      });
      if (!(offer.tiers || []).length && Number(offer.value) > 0) {
        var conditions = normalizedConditions(offer.conditions);
        var minimum = Math.max(1, conditions.minTotalQuantity,
          Math.ceil(conditions.minSubtotalCents / Math.max(1, Number(variant.price || 0))));
        conditions.items.concat(conditions.collectionItems).forEach(function (item) {
          minimum = Math.max(minimum, Number(item.minQuantity || item.quantity || 1));
        });
        if (Number.isFinite(minimum)) quantities.add(Math.ceil(minimum));
      }
    });
    var milestones = [];
    Array.from(quantities).sort(function (a, b) { return a - b; }).forEach(function (quantity) {
      var lines = [{ id: variantId, price: Number(variant.price || 0), quantity: quantity }];
      var cart = cartFromLines(safeConfig, lines);
      var percent = quote(safeConfig, lines, options).discountPercent;
      // Everything adds each unlocked tier's gift reward, rather than replacing it.
      var freeQuantity = offers.reduce(function (total, offer) {
        if (!eligible(offer, cart, purchaseOptionMode)) return total;
        return total + (offer.tiers || []).reduce(function (gifts, tier) {
          return gifts + (quantity >= Number(tier.minQuantity) ? tierGiftQuantity(tier) : 0);
        }, 0);
      }, 0);
      var previous = milestones[milestones.length - 1];
      if (percent <= 0 && freeQuantity <= 0) return;
      if (previous && previous.percentOff === percent && previous.freeQuantity === freeQuantity) return;
      milestones.push({ minQuantity: quantity, percentOff: percent, freeQuantity: freeQuantity });
    });
    return milestones;
  }

  function fetchConfig(productHandle, options) {
    var handle = String(productHandle || '').trim();
    var cacheKey = handle || '__global__';
    var fresh = Boolean(options && options.fresh);
    if (!fresh && configPromises.has(cacheKey)) return configPromises.get(cacheKey);
    var shop = document.documentElement.dataset.revliftShopDomain || (window.Shopify && window.Shopify.shop) || '';
    var url = new URL('/apps/everything-app', window.location.origin);
    if (shop) url.searchParams.set('shop', shop);
    if (handle) url.searchParams.set('product_handle', handle);
    if (fresh) url.searchParams.set('_theme_pricing', String(Date.now()));
    var promise = fetch(url.toString(), {
      cache: fresh ? 'no-store' : 'default',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('EA pricing config request failed.');
      return response.json();
    }).then(function (payload) {
      return (payload && payload.config) || {};
    }).catch(function (error) {
      if (!fresh) configPromises.delete(cacheKey);
      throw error;
    });
    if (!fresh) configPromises.set(cacheKey, promise);
    return promise;
  }

  function offerHasDiscount(offer) {
    if (!offer || offer.recommendationOnly) return false;
    if (clampPercent(offer.value) > 0 && String(offer.valueType || 'percentage') !== 'fixed') return true;
    if (Number(offer.value || 0) > 0 && String(offer.valueType || '') === 'fixed') return true;
    return (Array.isArray(offer.tiers) ? offer.tiers : []).some(function (tier) {
      return tierPercent(tier) > 0;
    });
  }

  function hasStoreWideDiscount(config) {
    var groups = [
      config && config.productOffers,
      config && config.pageOffers,
      config && config.orderOffers
    ];
    return groups.some(function (offers) {
      return (Array.isArray(offers) ? offers : []).some(function (offer) {
        return offer && offer.storeWide === true && offerHasDiscount(offer);
      });
    });
  }

  function storeWideDiscountPercent(config) {
    var groups = [
      config && config.productOffers,
      config && config.pageOffers,
      config && config.orderOffers
    ];
    return groups.reduce(function (best, offers) {
      return (Array.isArray(offers) ? offers : []).reduce(function (offerBest, offer) {
        if (!offer || offer.storeWide !== true || !offerHasDiscount(offer)) return offerBest;
        var valuePercent = String(offer.valueType || 'percentage') === 'fixed'
          ? 0
          : clampPercent(offer.value);
        var tierBest = (Array.isArray(offer.tiers) ? offer.tiers : []).reduce(function (tierValue, tier) {
          return Math.max(tierValue, tierPercent(tier));
        }, 0);
        return Math.max(offerBest, valuePercent, tierBest);
      }, best);
    }, 0);
  }

  function formatPercent(value) {
    var rounded = Math.round(Number(value || 0) * 100) / 100;
    return String(rounded);
  }

  function syncSaleBannerText(banners, config) {
    var percent = storeWideDiscountPercent(config);
    if (percent <= 0) return;
    banners.forEach(function (banner) {
      var message = banner.querySelector('[data-revlift-sale-message]');
      var fallbackText = banner.dataset.revliftSaleFallbackText || (message && message.textContent) || '';
      if (message) {
        message.textContent = fallbackText.replace(/\[percent\]|\{\{\s*percent\s*\}\}/g, formatPercent(percent));
      }
    });
  }

  function syncSaleBanner() {
    var banners = Array.from(document.querySelectorAll('[data-revlift-sale-banner]'));
    var standardAnnouncements = Array.from(document.querySelectorAll('[data-revlift-standard-announcement]'));
    fetchConfig('', { fresh: true }).then(function (config) {
      var active = hasStoreWideDiscount(config);
      window.RevLiftPricing.storeWideActive = active;
      syncSaleBannerText(banners, config);
      banners.forEach(function (banner) { banner.hidden = !active; });
      standardAnnouncements.forEach(function (announcement) { announcement.hidden = active; });
      document.dispatchEvent(new CustomEvent('revlift:storewide-state', {
        detail: { active: active, discountPercent: storeWideDiscountPercent(config) }
      }));
    }).catch(function () {
      window.RevLiftPricing.storeWideActive = false;
      banners.forEach(function (banner) { banner.hidden = true; });
      standardAnnouncements.forEach(function (announcement) { announcement.hidden = false; });
      document.dispatchEvent(new CustomEvent('revlift:storewide-state', { detail: { active: false } }));
    });
  }

  function renderLegacyPdpPrice(root, config, variant) {
    if (!root) return;
    var nativePrice = root.querySelector('.price:not([data-revlift-promo-price])');
    var promoPrice = root.querySelector('[data-revlift-promo-price]');
    if (!nativePrice || !promoPrice) return;

    var variantId = numericId(variant && variant.id) || numericId(root.dataset.revliftVariantId);
    var unitPrice = Number((variant && variant.price) || root.dataset.revliftVariantPrice || 0);
    var quoteResult = variantId && unitPrice > 0
      ? quote(config || {}, [{ id: variantId, price: unitPrice, quantity: 1 }], { purchaseOptionMode: 'one_time' })
      : null;
    var regularTotal = quoteResult ? Number(quoteResult.regularTotal || unitPrice) : unitPrice;
    var discountedTotal = quoteResult ? Number(quoteResult.discountedTotal || regularTotal) : regularTotal;
    var discountPercent = quoteResult ? clampPercent(quoteResult.discountPercent) : 0;
    var active = discountPercent > 0 && discountedTotal < regularTotal;

    nativePrice.hidden = active;
    promoPrice.hidden = !active;
    root.dataset.revliftDiscountActive = active ? 'true' : 'false';
    if (!active) {
      delete root.dataset.revliftDiscountPercent;
      return;
    }

    root.dataset.revliftVariantId = variantId;
    root.dataset.revliftVariantPrice = String(unitPrice);
    root.dataset.revliftDiscountPercent = String(discountPercent);
    var regularPrice = promoPrice.querySelector('[data-revlift-regular-price]');
    var salePrice = promoPrice.querySelector('[data-revlift-sale-price]');
    var moneyFormat = root.dataset.revliftMoneyFormat || '$__amount__';
    if (regularPrice) regularPrice.textContent = formatThemeMoney(regularTotal, moneyFormat);
    if (salePrice) salePrice.textContent = formatThemeMoney(discountedTotal, moneyFormat);
  }

  function loadLegacyPdpPrice(root, variant) {
    if (!root) return;
    var existingConfig = legacyPdpPriceConfigs.get(root);
    if (existingConfig) {
      renderLegacyPdpPrice(root, existingConfig, variant);
      return;
    }
    fetchConfig(root.dataset.revliftProductHandle || '')
      .then(function (config) {
        legacyPdpPriceConfigs.set(root, config || {});
        renderLegacyPdpPrice(root, config || {}, variant);
      })
      .catch(function () {
        renderLegacyPdpPrice(root, {}, variant);
      });
  }

  function syncLegacyPdpPrices() {
    Array.from(document.querySelectorAll('[data-revlift-legacy-pdp-price]')).forEach(function (root) {
      loadLegacyPdpPrice(root);
    });
  }

  window.RevLiftPricing = {
    fetchConfig: fetchConfig,
    quote: quote,
    quantityTiers: quantityTiers,
    hasDiscount: offerHasDiscount,
    hasStoreWideDiscount: hasStoreWideDiscount,
    storeWideDiscountPercent: storeWideDiscountPercent,
    syncLegacyPdpPrices: syncLegacyPdpPrices,
    storeWideActive: null
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncSaleBanner();
      syncLegacyPdpPrices();
    }, { once: true });
  } else {
    syncSaleBanner();
    syncLegacyPdpPrices();
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var detail = event && event.data;
      var sectionId = detail && detail.sectionId;
      var root = sectionId ? document.getElementById('price-' + sectionId) : null;
      if (root && root.matches('[data-revlift-legacy-pdp-price]')) {
        loadLegacyPdpPrice(root, detail.variant);
      }
    });
  }
  document.dispatchEvent(new CustomEvent('revlift:pricing-ready'));
})();
